/**
 * The content of this file is almost a copy from
 * https://github.com/clauderic/react-sortable-hoc/blob/6e8fe9f9bdf36cd59f7d9d46ee31560ec9d52ff8/src/utils.js
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2016, Claudéric Demers
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 */


export const events = {
  start: ["touchstart", "mousedown"],
  move: ["touchmove", "mousemove"],
  end: ["touchend", "touchcancel", "mouseup"],
};

export const vendorPrefix = (() => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    // Server environment
    return "";
  }
  
  // fix for: https://bugzilla.mozilla.org/show_bug.cgi?id=548397
  // window.getComputedStyle() returns null inside an iframe with display: none
  // in this case return an array with a fake mozilla style in it.
  const styles = window.getComputedStyle(document.documentElement, "") || [
    "-moz-hidden-iframe",
  ];
  const pre = (() => {
    const prefix = Array.prototype.slice
      .call(styles)
      .join("")
      .match(/-(moz|webkit|ms)-/);
    if(prefix && typeof(prefix[1]) === "string"){
      return prefix[1];
    }
    
    // @ts-ignore
    if(styles.OLink === ""){
      return "o";
    }
    
    return undefined;
  })();
  
  switch (pre) {
    case "ms":
      return "ms";
    default:
      return pre && pre.length ? pre[0].toUpperCase() + pre.substr(1) : "";
  }
})();

export function closest(el: Node|null, fn: (el: Node) => boolean): Node|null {
  while (el) {
    if (fn(el)) {
      return el;
    }
    
    el = el.parentNode;
  }
  
  return null;
}

export function limit(min: number, max: number, value: number): number {
  return Math.max(min, Math.min(value, max));
}

function getPixelValue(stringValue: string): number {
  if (stringValue.substr(-2) === "px") {
    return parseFloat(stringValue);
  }
  
  return 0;
}

export function getElementMargin(element: HTMLElement) {
  const style = (() => {
    if(!element.parentNode){
      return window.getComputedStyle(element);
    }
    return window.getComputedStyle((element.parentNode as HTMLElement));
  })();
  
  return {
    top: getPixelValue(style.paddingTop||""),
    right: getPixelValue(style.paddingRight||""),
    bottom: getPixelValue(style.paddingBottom||""),
    left: getPixelValue(style.paddingLeft||""),
  };
}

export function getPosition(event: Event) {
  if ((event as TouchEvent).touches && (event as TouchEvent).touches.length) {
    return {
      x: (event as TouchEvent).touches[0].pageX,
      y: (event as TouchEvent).touches[0].pageY,
    };
  }
  else if ((event as TouchEvent).changedTouches && (event as TouchEvent).changedTouches.length) {
    return {
      x: (event as TouchEvent).changedTouches[0].pageX,
      y: (event as TouchEvent).changedTouches[0].pageY,
    };
  }
  else {
    return {
      x: (event as MouseEvent).pageX,
      y: (event as MouseEvent).pageY,
    };
  }
}

export function isTouchEvent(event: Event) {
  return (
    ((event as TouchEvent).touches && (event as TouchEvent).touches.length) ||
    ((event as TouchEvent).changedTouches && (event as TouchEvent).changedTouches.length)
  );
}

export function getEdgeOffset(node: HTMLElement){
  // let parent = node.parentNode;
  // Get the actual offsetTop / offsetLeft value, no matter how deep the node is nested
  return {
    top: node.offsetTop,
    left: node.offsetLeft,
    width: node.offsetWidth,
    height: node.offsetHeight,
  };
}

export function getLockPixelOffset({lockOffset, width, height}: {lockOffset: number, width: number, height: number}) {
  let offsetX = lockOffset;
  let offsetY = lockOffset;
  let unit = "px";
  
  if (typeof lockOffset === "string") {
    const match = /^[+-]?\d*(?:\.\d*)?(px|%)$/.exec(lockOffset);
    
    offsetX = parseFloat(lockOffset);
    offsetY = parseFloat(lockOffset);
    if(match && match.length > 0){
      unit = match[1];
    }
  }
  
  if (unit === "%") {
    offsetX = (offsetX * width) / 100;
    offsetY = (offsetY * height) / 100;
  }
  
  return {
    x: offsetX,
    y: offsetY,
  };
}

/**
 * @param {{top: number, left: number, width: number, height: number}} hitZone
 * @param {{top: number, left: number}} pointer
 * @returns {boolean}
 */
export function hitTest(hitZone: {top: number, left: number, width: number, height: number}, pointer: {top: number, left: number}){
  const {top: t, left: l, width: w, height: h} = hitZone;
  const {top, left} = pointer;
  
  return (t <= top && top <= t + h)
    && (l <= left && left <= l + w);
}
